import { useEffect, useState, type ComponentType } from 'react';
import { ApiError, NetworkError, api, type DashboardData } from '../api';
import { useI18n } from '../i18n';
import { AppointmentStatus, ErrorState, Skeleton } from '../components';
import { IconAlert, IconBox, IconCalendar, IconChevron, IconClock, IconFlask, IconPatients, IconWallet } from '../icons';
import { formatMinor } from '../money';
import { FlowLine } from '../brand';
import { MissedFollowups } from '../components/MissedFollowups';
import clinicPhoto from '../assets/brand/clinic-hero.webp';

/**
 * The day, at a glance.
 *
 * Two panels, and they do different jobs. TODAY'S SCHEDULE is what is about to
 * happen. NEEDS ATTENTION is what will go wrong if nobody looks — stock that
 * has run out, lab work coming due, money nobody has chased. A dashboard that
 * only shows the first is a diary; a dashboard that mixes the two into one
 * feed makes both harder to read.
 *
 * Every figure is labelled for what it actually measures. "Patients" counts
 * patient RECORDS, not visits, and says so.
 */
export function Dashboard({
  clinicId,
  userName,
  permissions = [],
  onGo,
}: {
  clinicId: string;
  userName: string;
  permissions?: string[];
  onGo?: (page: string) => void;
}) {
  const { t, locale, formatTime, formatNumber, personName } = useI18n();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Clearing on clinic change matters: a stale total from the previous clinic
    // must never be visible, even for a frame (spec §4, T01).
    setData(null);
    setError(null);
    api
      .dashboard()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof NetworkError ? t.errors.network : e instanceof ApiError ? e.message : t.common.error);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  if (!data) {
    return (
      <>
        <div className="stats">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="card stat">
              <div className="skeleton" style={{ height: 30, width: 66 }} />
              <div className="skeleton" style={{ height: 13, width: 108, marginTop: 9 }} />
            </div>
          ))}
        </div>
        <div className="card">
          <Skeleton rows={5} />
        </div>
      </>
    );
  }

  const tz = data.clinic.timezone;

  return (
    <>
      {/* The welcome banner, as the brand's dashboard reference draws it. */}
      <section className="hero-card" aria-label={t.dashboard.title}>
        <div className="hero-copy">
          <div className="page-eyebrow">{t.dashboard.welcomeBack}</div>
          <h1>{userName}</h1>
          <p className="page-sub">{t.dashboard.todayIntro}</p>
          <div className="hero-meta">
            {locale === 'ar' ? data.clinic.nameAr : data.clinic.nameEn} · {tz}
          </div>
        </div>
        <div className="hero-media" aria-hidden="true">
          <img src={clinicPhoto} alt="" />
        </div>
        <span className="hero-band" aria-hidden="true" />
        <FlowLine className="hero-line" />
      </section>

      <div className="stats">
        <Stat
          value={formatNumber(data.counts.uniquePatients)}
          label={t.dashboard.uniquePatients}
          /* Labelled precisely: this counts patient RECORDS, not visits. */
          hint={data.counts.patientScope === 'assigned' ? t.dashboard.assignedOnly : t.dashboard.uniquePatientsHint}
          Icon={IconPatients}
          tone="teal"
        />
        <Stat
          value={formatNumber(data.counts.appointmentsToday)}
          label={t.dashboard.appointmentsToday}
          hint={tz}
          Icon={IconCalendar}
          tone="blue"
        />
        <Stat
          value={formatNumber(data.counts.upcomingAppointments)}
          label={t.dashboard.upcoming}
          hint={t.dashboard.upcomingHint}
          Icon={IconClock}
          tone="violet"
        />
        <Stat
          value={formatNumber(data.counts.activeMedicalAlerts)}
          label={t.dashboard.alerts}
          Icon={IconAlert}
          tone="amber"
        />
        {/* Work done and never charged for. On the front page because the
            failure it names is exactly "nobody noticed". */}
        {data.unbilledWork ? (
          <Stat
            value={formatMinor(data.unbilledWork.totalMinor, data.clinic.currency, locale)}
            label={t.dashboard.unbilledWork}
            hint={`${formatNumber(data.unbilledWork.count)} · ${t.dashboard.unbilledWorkHint}`}
            Icon={IconAlert}
            tone={data.unbilledWork.count > 0 ? 'amber' : 'teal'}
          />
        ) : null}
      </div>

      {/* Patients who did not come, until someone follows them up. */}
      {permissions.includes('appointment.read') ? <MissedFollowups compact canWrite={permissions.includes('appointment.write')} /> : null}

      <div className="split">
        <div className="card">
          <div className="card-head">
            <h2>
              {t.dashboard.todaySchedule}
              <span className="count-chip">{formatNumber(data.today.length)}</span>
            </h2>
            {onGo ? (
              <button type="button" className="btn-link" onClick={() => onGo('appointments')}>
                {t.nav.appointments}
              </button>
            ) : null}
          </div>
          {data.today.length === 0 ? (
            <div className="empty">{t.dashboard.noAppointments}</div>
          ) : (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>{t.appointments.time}</th>
                    <th>{t.appointments.patient}</th>
                    <th>{t.appointments.reason}</th>
                    <th>{t.appointments.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.today.map((a) => (
                    <tr key={a.id}>
                      <td className="ltr-value" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {formatTime(a.startsAt, tz)}–{formatTime(a.endsAt, tz)}
                      </td>
                      <td>{a.patient ? personName(a.patient) : '—'}</td>
                      <td style={{ color: 'var(--ink-2)' }}>{a.reason ?? '—'}</td>
                      <td>
                        <AppointmentStatus status={a.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          <Attention permissions={permissions} clinicId={clinicId} onGo={onGo} />

          <div className="card">
            <div className="card-head">
              <h2>{t.dashboard.recentActivity}</h2>
            </div>
            {data.recentActivity.length === 0 ? (
              <div className="empty">{t.dashboard.noActivity}</div>
            ) : (
              <div className="card-body stack" style={{ gap: 11 }}>
                {data.recentActivity.map((r) => (
                  <div key={r.id} className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
                    <span
                      className="ltr-value"
                      style={{
                        color: 'var(--ink-3)',
                        fontSize: 12,
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: 42,
                      }}
                    >
                      {formatTime(r.at, tz)}
                    </span>
                    <span style={{ flex: 1 }}>
                      <span style={{ display: 'block' }}>{r.summary ?? r.action}</span>
                      <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>{r.actor}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * What will go wrong if nobody looks.
 *
 * Each line is fetched only if this person is allowed to see it — a
 * receptionist does not get a patient-balance figure just because the panel has
 * room for one. A row appears only when there is genuinely something to act on,
 * so an empty panel means "nothing needs you", not "nothing loaded".
 */
function Attention({
  permissions,
  clinicId,
  onGo,
}: {
  permissions: string[];
  clinicId: string;
  onGo?: (page: string) => void;
}) {
  const { t, locale, formatNumber } = useI18n();
  const [lowStock, setLowStock] = useState<number | null>(null);
  const [openLabs, setOpenLabs] = useState<number | null>(null);
  const [outstanding, setOutstanding] = useState<{ minor: number; currency: string } | null>(null);

  const can = (p: string) => permissions.includes(p);

  useEffect(() => {
    setLowStock(null);
    setOpenLabs(null);
    setOutstanding(null);

    // Each is optional: a plan without the module, or a role without the
    // permission, simply contributes no line rather than an error.
    if (can('inventory.read')) {
      api
        .inventoryAlerts()
        .then((r) => setLowStock(r.lowStock.length))
        .catch(() => setLowStock(null));
    }
    if (can('lab.read')) {
      api
        .labOrders()
        .then((r) => setOpenLabs(r.orders.filter((o) => !['delivered', 'cancelled'].includes(o.status)).length))
        .catch(() => setOpenLabs(null));
    }
    if (can('finance.reports.read')) {
      api
        .financeReports()
        .then((r) => setOutstanding({ minor: r.figures.outstandingMinor, currency: r.currency }))
        .catch(() => setOutstanding(null));
    }
    // `permissions` is stable for a given clinic, so the clinic id is the real
    // dependency here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  const rows: { key: string; Icon: ComponentType<{ size?: number }>; label: string; value: string; page: string }[] = [];
  if (lowStock !== null && lowStock > 0) {
    rows.push({
      key: 'stock',
      Icon: IconBox,
      label: t.inventory.lowStock,
      value: `${formatNumber(lowStock)} ${t.inventory.materials}`,
      page: 'inventory',
    });
  }
  if (openLabs !== null && openLabs > 0) {
    rows.push({
      key: 'lab',
      Icon: IconFlask,
      label: t.lab.openOrders,
      value: `${formatNumber(openLabs)} ${t.lab.orders}`,
      page: 'laboratory',
    });
  }
  if (outstanding !== null && outstanding.minor > 0) {
    rows.push({
      key: 'money',
      Icon: IconWallet,
      label: t.finance.outstanding,
      value: formatMinor(outstanding.minor, outstanding.currency, locale),
      page: 'finance',
    });
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>
          {t.dashboard.attention}
          {rows.length > 0 ? <span className="count-chip">{rows.length}</span> : null}
        </h2>
      </div>
      {rows.length === 0 ? (
        <div className="card-body" style={{ color: 'var(--ink-3)' }}>
          {t.dashboard.nothingNeedsAttention}
        </div>
      ) : (
        <div className="card-body stack" style={{ gap: 4 }}>
          {rows.map((r) => (
            <button
              key={r.key}
              type="button"
              className="menu-item"
              onClick={() => onGo?.(r.page)}
              style={{ padding: '10px 8px' }}
            >
              <span className="stat-icon amber">
                <r.Icon size={17} />
              </span>
              <span style={{ flex: 1 }}>
                <strong style={{ display: 'block', fontWeight: 550 }}>{r.label}</strong>
                <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                  <bdi dir="ltr">{r.value}</bdi>
                </span>
              </span>
              <IconChevron size={14} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({
  value,
  label,
  hint,
  Icon,
  tone,
}: {
  value: string;
  label: string;
  hint?: string;
  Icon: ComponentType<{ size?: number }>;
  tone: string;
}) {
  return (
    <div className={value.length > 6 ? 'card stat stat-wide' : 'card stat'}>
      <div className="stat-top">
        <span>{label}</span>
        <span className={`stat-icon ${tone}`} aria-hidden="true">
          <Icon size={18} />
        </span>
      </div>
      {/* Isolated left-to-right, so "US$0.00" reads the same inside Arabic text. */}
      <div className="stat-value">
        <bdi dir="ltr">{value}</bdi>
      </div>
      {hint ? <div className="stat-hint">{hint}</div> : null}
    </div>
  );
}
